import { useMemo, useState } from 'react';
import { Button } from '~/components/ui';
import { useCart } from '~/lib/cart';
import { currency } from '~/lib/format';
import { Transition } from '@headlessui/react';
import { Link } from '~/components/global';
import Image from 'next/image';
import asplus from '~/public/as-plus-2.svg';
import useUser from '~/lib/user';
import useSumoDay from '~/lib/util/sumoDay';
import { Dropdown } from '@appsumo/dorado-react';
import { Deal, Plan } from '~/types/deal';

const COMING_SOON_BANNER_TEXT = 'Coming soon!';

const InfoOrInactiveDeal = ({
  deal,
  inactiveIndefinitely,
}: {
  deal: any;
  inactiveIndefinitely: boolean;
}) => {
  return (
    <div>
      {deal.is_info && (
        /* Info deal */
        <>
          <strong>Want to see {deal.public_name} on AppSumo?</strong> Let us
          know &mdash; and make sure you never miss an amazing deal!
        </>
      )}
      {inactiveIndefinitely && (
        /* Inactive deal */
        <>
          <strong>This deal is unavailable (a bummer, we know).</strong>{' '}
          Don&apos;t miss a great deal again — drop your email to get all our
          latest tools!
        </>
      )}
      <div>
        TODO: email input
        <br />
        <Button>
          {deal.is_info ? 'Yes, bring to AppSumo' : 'Stay in the know'}
        </Button>
      </div>
    </div>
  );
};

const formatPlan = (deal: any, plan: any) => {
  return (
    <>
      {deal.use_licensing ? plan.public_name : `${plan.codes} Codes`} :{' '}
      {currency(plan.price)}{' '}
      <span className="line-through">{currency(plan.original_price)}</span>
    </>
  );
};

const DealWithPlans = ({
  deal,
  selectedPlan,
  onChange,
}: {
  deal: any;
  selectedPlan: any | null;
  onChange: (planId: number) => void;
}) => {
  // generate list of plans (codes or licensing) for the select box
  const options = useMemo(
    () =>
      deal.plans.map((plan: any) => ({
        value: plan.id,
        label: formatPlan(deal, plan),
      })),
    [deal],
  );

  return (
    <div className="flex flex-col">
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor="plan" className="font-bold">
          Select a plan
        </label>
        <Link
          href={`${deal.get_absolute_url}#pricePlans`}
          className="text-blue-600 hover:underline"
        >
          View plan details
        </Link>
      </div>
      <Dropdown
        className="w-full"
        options={options}
        defaultValue={{
          value: selectedPlan?.id,
          label: (selectedPlan
            ? formatPlan(deal, selectedPlan)
            : '-- Select a plan --') as string,
        }}
        onClick={(value) => onChange(parseInt(String(value.value), 10))}
      />
    </div>
  );
};

const DealOneTimePurchase = ({
  deal,
  selectedPlan,
}: {
  deal: Deal;
  selectedPlan?: Plan;
}) => {
  const percentSavings: number = useMemo(() => {
    if (!Number(selectedPlan?.price) || !Number(selectedPlan?.original_price)) {
      return 0;
    }

    return Math.round(
      (1 -
        Number(selectedPlan?.price || 0) /
          Number(selectedPlan?.original_price || 0)) *
        100,
    );
  }, [selectedPlan]);

  return (
    <div className="mb-4 flex flex-col">
      {!!deal.default_price && (
        <h6 className="font-header text-sm font-semibold">
          One time purchase of
        </h6>
      )}
      <div className="inline-flex items-center gap-x-2">
        {percentSavings > 0 && (
          <span className="text-2xl font-bold text-brick">
            -{percentSavings}%
          </span>
        )}
        <span className="text-2xl font-medium">{currency(deal.price)}</span>
        {percentSavings === 0 && (
          <span className="text-sm line-through">
            {currency(deal.original_price)}
          </span>
        )}
      </div>
      {percentSavings > 0 && (
        <div className="text-sm text-grace line-through">
          {currency(deal.original_price)}
        </div>
      )}
    </div>
  );
};

export const BuyNow = ({ deal, inView }: { deal: Deal; inView: boolean }) => {
  const { lineItemAdd } = useCart();
  const { user } = useUser();
  const { isSumoDay } = useSumoDay();
  const [planId, setPlanId] = useState<number | null>(deal.default_plan.id);

  // is the deal early access or last call?
  const isEarlyAccessOrLastCall = useMemo(
    () => deal.buy_button.is_early_access || deal.buy_button.is_last_call,
    [deal.buy_button.is_early_access, deal.buy_button.is_last_call],
  );

  // is the deal inactive indefinitely?
  const inactiveIndefinitely = useMemo(
    () => deal.stage_details?.is_inactive_indefinitely || false,
    [deal.stage_details?.is_inactive_indefinitely],
  );
  const hasPlans = useMemo(() => deal.plans.length > 1, [deal.plans.length]);

  // is the deal coming soon?
  const isComingSoon = useMemo(
    () =>
      !isEarlyAccessOrLastCall &&
      (deal.is_coming_soon ||
        deal.banner_details.text === COMING_SOON_BANNER_TEXT),
    [deal.is_coming_soon, deal.banner_details.text, isEarlyAccessOrLastCall],
  );

  // buy button with the appropriate text if it can be bought
  const buyButton = useMemo(() => {
    if (deal.is_info || inactiveIndefinitely || deal.buy_button.is_ended) {
      return null;
    }

    const buyButtonText = () => {
      if (deal.buy_button.is_draft) {
        return 'Buy now (Draft)';
      }

      if (isComingSoon) {
        return 'Coming soon';
      }

      if (deal.buy_button.is_ended) {
        return 'Sold out';
      }

      if (deal.buy_button.is_get_now) {
        return deal.is_free ? 'Get now for FREE' : 'Get now';
      }

      return 'Buy now';
    };

    if (isEarlyAccessOrLastCall) {
      return user && user.has_plus ? (
        /* Plus user can buy now */
        <Button className="inline-flex items-center gap-x-2 font-header">
          Buy now with
          <Image src={asplus} height={14} alt="Buy now with AppSumo Plus" />
        </Button>
      ) : (
        /* Extended access and no plus needs to join */
        <Button className="inline-flex items-center gap-x-2 font-header">
          Join
          <Image src={asplus} height={14} alt="Join AppSumo Plus to buy now" />
          to buy now
        </Button>
      );
    }

    /* Normal deal state can be bought */
    return (
      <Button
        onClick={() =>
          lineItemAdd(deal.id, planId, isSumoDay ? null : 'SUMOCHAT10')
        }
        className="w-full"
      >
        {buyButtonText()}
      </Button>
    );
  }, [
    isEarlyAccessOrLastCall,
    isComingSoon,
    isSumoDay,
    inactiveIndefinitely,
    user,
    lineItemAdd,
    deal.id,
    planId,
    deal.buy_button.is_draft,
    deal.buy_button.is_ended,
    deal.buy_button.is_get_now,
    deal.is_free,
    deal.is_info,
  ]);

  const selectedPlan = useMemo(
    () => deal.plans.find((plan: any) => plan.id === planId),
    [planId, deal.plans],
  );

  return (
    <>
      <div
        className={
          'my-4 flex flex-col gap-y-4 max-sm:border-t max-sm:pt-4 max-sm:text-center'
        }
      >
        {(deal.is_info || inactiveIndefinitely) && (
          <InfoOrInactiveDeal
            deal={deal}
            inactiveIndefinitely={inactiveIndefinitely}
          />
        )}
        {!deal.buy_button.is_ended && !inactiveIndefinitely && (
          <>
            {hasPlans ? (
              <DealWithPlans
                deal={deal}
                selectedPlan={selectedPlan}
                onChange={(planId) => setPlanId(planId)}
              />
            ) : (
              <DealOneTimePurchase deal={deal} selectedPlan={selectedPlan} />
            )}
            {buyButton}
          </>
        )}
      </div>
      {/* when user scrolls down show a fixed mobile buy bar */}
      {buyButton && (
        <Transition
          show={!inView}
          enter="transition-opacity ease-in duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed bottom-0 left-0 z-9 flex w-full items-center gap-x-4 bg-white p-4 shadow-inner sm:hidden"
        >
          {hasPlans && (
            <div className="grow">
              <DealWithPlans
                deal={deal}
                selectedPlan={selectedPlan}
                onChange={(planId) => setPlanId(planId)}
              />
            </div>
          )}
          <div className="shrink grow">{buyButton}</div>
        </Transition>
      )}
    </>
  );
};
